import * as THREE from 'three';
import { adoptGlow } from '../materials.ts';
import type { BakedPart } from './bake.ts';
import { ModelBatch } from './batch.ts';
import type { GlowLayer } from './glow.ts';
import { baked } from './library.ts';
import { farModel, withIndex } from './lod.ts';

/** What the built structures are drawn through (a ModelBatch's own calls). */
export type StructSink = Pick<ModelBatch, 'add' | 'addDynamic' | 'hasDynamic'>;

/** Opaque parts draw in any order with the same picture: those go together. Translucent ones keep their batch. */
const mergeable = (m: THREE.Material): boolean => !m.transparent && !(m as THREE.ShaderMaterial).isShaderMaterial;

const WHITE = new THREE.Color(1, 1, 1);

const TWINS = new WeakMap<THREE.Material, THREE.Material>();

/**
 * The merged twin of a material: the same look, a material of its own. three keeps one program per material and
 * object kind; a material drawn both batched (here) and instanced (works, props, the keep) would switch and
 * re-key its program on every object, every frame, which cost more than the merge saved.
 */
const twin = (m: THREE.Material): THREE.Material => {
  let t = TWINS.get(m);
  if (t) return t;
  t = m.clone();
  t.onBeforeCompile = m.onBeforeCompile;
  t.customProgramCacheKey = m.customProgramCacheKey;
  t.forceSinglePass = m.forceSinglePass;
  t.name = `${m.name}+built`;
  // Self-lit ones brighten at night like the original.
  adoptGlow(t);
  TWINS.set(m, t);
  return t;
};

/** Every opaque part of every built structure in one material: one multi-draw call (three's BatchedMesh). */
class Merged {
  readonly mesh: THREE.BatchedMesh;
  private ids = new Map<THREE.BufferGeometry, number>();
  private inst: number[] = [];
  private shown: boolean[] = [];
  /**
   * What each slot was last given (geometry, matrix, colour): written again only when it changes. Every write marks
   * the mesh's matrix or colour texture to upload, and the board is drawn again each tick with mostly the same
   * places: ~20 texture uploads a tick, 16 % of three's per-draw setup on a phone in a big wave (2026-11-01).
   */
  private gids: number[] = [];
  private mats = new Float32Array(64 * 16);
  private cols = new Float32Array(64 * 3);
  private cursor = 0;
  private verts = 0;
  private maxVerts = 1 << 15;
  private indices = 0;
  private maxIndices: number;
  private maxInst = 64;

  /** `indexed`: every geometry added comes with an index (the far versions, engine/lod.ts). */
  constructor(material: THREE.Material, indexed = false) {
    this.maxIndices = indexed ? 1 << 15 : 0;
    this.mesh = new THREE.BatchedMesh(this.maxInst, this.maxVerts, this.maxIndices, material);
    // Drawn in the order added, every instance (as the instanced meshes did): nothing to sort or cull per frame.
    this.mesh.sortObjects = false;
    this.mesh.perObjectFrustumCulled = false;
    this.mesh.frustumCulled = false;
    this.mesh.name = `built:${material.name || material.type}`;
  }

  begin(): void {
    this.cursor = 0;
  }

  add(geo: THREE.BufferGeometry, matrix: THREE.Matrix4, color: THREE.Color | undefined): void {
    let gid = this.ids.get(geo);
    if (gid === undefined) {
      const n = geo.getAttribute('position').count;
      const ni = geo.index?.count ?? 0;
      if (this.verts + n > this.maxVerts || this.indices + ni > this.maxIndices) {
        while (this.verts + n > this.maxVerts) this.maxVerts *= 2;
        while (this.indices + ni > this.maxIndices) this.maxIndices *= 2;
        this.mesh.setGeometrySize(this.maxVerts, this.maxIndices);
      }
      gid = this.mesh.addGeometry(geo);
      this.verts += n;
      this.indices += ni;
      this.ids.set(geo, gid);
    }
    let iid = this.inst[this.cursor];
    if (iid === undefined) {
      if (this.inst.length >= this.maxInst) {
        this.maxInst *= 2;
        this.mesh.setInstanceCount(this.maxInst);
      }
      iid = this.mesh.addInstance(gid);
      this.inst.push(iid);
      this.shown.push(true);
      this.gids.push(gid);
      if (this.mats.length < this.inst.length * 16) {
        const m = new Float32Array(this.maxInst * 16);
        m.set(this.mats);
        this.mats = m;
        const c = new Float32Array(this.maxInst * 3);
        c.set(this.cols);
        this.cols = c;
      }
      this.write(this.cursor, iid, matrix, color ?? WHITE);
    } else {
      const c = this.cursor;
      if (this.gids[c] !== gid) {
        this.mesh.setGeometryIdAt(iid, gid);
        this.gids[c] = gid;
      }
      if (!this.shown[c]) {
        this.mesh.setVisibleAt(iid, true);
        this.shown[c] = true;
      }
      const e = matrix.elements;
      const o = c * 16;
      let moved = false;
      for (let k = 0; k < 16 && !moved; k++) moved = this.mats[o + k] !== Math.fround(e[k] as number);
      const col = color ?? WHITE;
      const q = c * 3;
      if (moved) {
        this.mesh.setMatrixAt(iid, matrix);
        this.mats.set(e, o);
      }
      if (this.cols[q] !== Math.fround(col.r) || this.cols[q + 1] !== Math.fround(col.g) || this.cols[q + 2] !== Math.fround(col.b)) {
        this.mesh.setColorAt(iid, col);
        this.cols[q] = col.r;
        this.cols[q + 1] = col.g;
        this.cols[q + 2] = col.b;
      }
    }
    this.cursor++;
  }

  private write(slot: number, iid: number, matrix: THREE.Matrix4, color: THREE.Color): void {
    this.mesh.setMatrixAt(iid, matrix);
    this.mesh.setColorAt(iid, color);
    this.mats.set(matrix.elements, slot * 16);
    this.cols[slot * 3] = color.r;
    this.cols[slot * 3 + 1] = color.g;
    this.cols[slot * 3 + 2] = color.b;
  }

  end(): void {
    for (let i = this.cursor; i < this.inst.length; i++) {
      if (!this.shown[i]) continue;
      this.mesh.setVisibleAt(this.inst[i] as number, false);
      this.shown[i] = false;
    }
    this.mesh.visible = this.cursor > 0;
  }
}

/** One structure model: its translucent parts and glows in a batch of its own, its opaque parts merged. */
class BuiltBatch implements StructSink {
  private tmp = new THREE.Matrix4();
  private tmp2 = new THREE.Matrix4();

  constructor(
    private model: ModelBatch,
    private frames: { geo: THREE.BufferGeometry; into: Merged }[][],
    private dyn: Map<string, { base: THREE.Matrix4; parts: { geo: THREE.BufferGeometry; into: Merged }[] }>,
  ) {}

  add(matrix: THREE.Matrix4, frame: number, color?: THREE.Color): void {
    this.model.add(matrix, frame, color);
    const n = this.frames.length;
    for (const p of this.frames[((frame % n) + n) % n] ?? []) p.into.add(p.geo, matrix, color);
  }

  addDynamic(name: string, matrix: THREE.Matrix4, local: THREE.Matrix4, color?: THREE.Color): void {
    this.model.addDynamic(name, matrix, local, color);
    const d = this.dyn.get(name);
    if (!d?.parts.length) return;
    // As ModelBatch places a dynamic part: entity × pivot × local animation.
    this.tmp.multiplyMatrices(matrix, d.base);
    this.tmp2.multiplyMatrices(this.tmp, local);
    for (const p of d.parts) p.into.add(p.geo, this.tmp2, color);
  }

  hasDynamic(name: string): boolean {
    return this.model.hasDynamic(name);
  }

  begin(): void {
    this.model.begin();
  }

  end(): void {
    this.model.end();
  }
}

/**
 * The built structures of a board in a handful of draw calls. Each kind, level, material and wear of building
 * was a batch of its own with an instanced mesh per material, some 150 draw calls on a big bastion; their
 * opaque parts now share one multi-draw per material (moving parts included: turrets, arms, gears), and only the
 * translucent ones and the glows stay with each model. Filled once a tick (see BattleView.drawBuilt).
 */
export class StructBatcher {
  readonly group = new THREE.Group();
  private merged = new Map<THREE.Material, Merged>();
  private batches = new Map<string, BuiltBatch>();

  /** `far`: the structures' far versions (engine/lod.ts), for a board seen small; the full ones until they can be made. */
  constructor(
    private glow: GlowLayer,
    private readonly far = false,
  ) {
    this.group.name = far ? 'built:far' : 'built';
  }

  private into(m: THREE.Material): Merged {
    let x = this.merged.get(m);
    if (!x) {
      x = new Merged(twin(m), this.far);
      this.merged.set(m, x);
      this.group.add(x.mesh);
    }
    return x;
  }

  batch(key: string): StructSink {
    let b = this.batches.get(key);
    if (!b) {
      const full = baked(key).baked;
      const model = this.far ? farModel(full) : full;
      if (!model) return this.fallback(key);
      const keep = (p: BakedPart): boolean => !mergeable(p.material);
      // The far merges are indexed throughout: a part too small to simplify goes in through an index of its own.
      const geoOf = (g: THREE.BufferGeometry): THREE.BufferGeometry => (this.far ? withIndex(g) : g);
      const parts = (ps: BakedPart[]): { geo: THREE.BufferGeometry; into: Merged }[] => ps.filter((p) => !keep(p)).map((p) => ({ geo: geoOf(p.geometry), into: this.into(p.material) }));
      const mb = new ModelBatch(model, this.glow, 8, keep);
      this.group.add(mb.group);
      b = new BuiltBatch(
        mb,
        model.frames.map((f) => parts(f.parts)),
        new Map(model.dynamic.map((d) => [d.name, { base: d.base, parts: parts(d.frame.parts) }])),
      );
      b.begin();
      this.batches.set(key, b);
    }
    return b;
  }

  /** The far batcher before the simplifier is ready: a full batcher of its own draws meanwhile. */
  private full: StructBatcher | null = null;

  private fallback(key: string): StructSink {
    if (!this.full) {
      this.full = new StructBatcher(this.glow);
      this.group.add(this.full.group);
      this.full.begin();
    }
    return this.full.batch(key);
  }

  begin(): void {
    for (const m of this.merged.values()) m.begin();
    for (const b of this.batches.values()) b.begin();
    this.full?.begin();
  }

  end(): void {
    for (const m of this.merged.values()) m.end();
    for (const b of this.batches.values()) b.end();
    this.full?.end();
  }
}
